import { ItemKey } from "./types";

export type RangeSelectionInput = {
  /**
   * Selectable keys in rendered order. Excludes anything hidden or unpickable
   * (collapsed groups, disabled options) — this is what bounds a range.
   */
  orderedKeys: ItemKey[];
  selectedKeys: ItemKey[];
  /** Last key toggled by a plain click, or null when there is no range to extend. */
  anchorKey: ItemKey | null;
  clickedKey: ItemKey;
  shiftKey: boolean;
  /** The checkbox's state after the click, applied to the whole range. */
  selected: boolean;
};

export type RangeSelectionResult = {
  selectedKeys: ItemKey[];
  /** The new anchor: always the clicked key. */
  anchorKey: ItemKey;
};

export function applyRangeSelection({
  orderedKeys,
  selectedKeys,
  anchorKey,
  clickedKey,
  shiftKey,
  selected,
}: RangeSelectionInput): RangeSelectionResult {
  const next = new Set(selectedKeys);
  const apply = (key: ItemKey) =>
    selected ? next.add(key) : next.delete(key);

  const clickedIndex = orderedKeys.indexOf(clickedKey);
  const anchorIndex =
    anchorKey == null ? -1 : orderedKeys.indexOf(anchorKey);

  // Both ends must be rendered; either missing means the list moved under the
  // anchor, so toggle just the clicked key rather than guess at a range.
  const extendsRange =
    shiftKey &&
    anchorIndex !== -1 &&
    clickedIndex !== -1 &&
    anchorIndex !== clickedIndex;

  if (extendsRange) {
    const [start, end] =
      anchorIndex < clickedIndex
        ? [anchorIndex, clickedIndex]
        : [clickedIndex, anchorIndex];

    for (let i = start; i <= end; i++) apply(orderedKeys[i]);
  } else {
    apply(clickedKey);
  }

  return { selectedKeys: [...next], anchorKey: clickedKey };
}
